import { Action, createReducer, on } from "@ngrx/store";
import * as settingsActions from "./settings.action";
import * as errorActions from "src/app/core/error/ngrx/error.actions";
import { EducationCategoryDto } from "../interfaces/education_category_dto";
import { EducationTitleDto } from "../interfaces/education_title_dto";
import { EducationBodyDto } from "../interfaces/education_body_dto";
import { LessonRatingDto } from "src/app/lessons/interfaces/lesson_rating";
import { ServiceDto } from "../interfaces/service_dto";
import { SubscriptionDto } from "../interfaces/subscription_dto";

export const settingFeatureKey = "setting";

export interface State {
  educationCategories: EducationCategoryDto[] | null;
  educationTitles: EducationTitleDto[] | null;
  educationBodies: EducationBodyDto[] | null;
  lessonRatings: LessonRatingDto[] | null;
  selectedEducationCategory: number | null;
  selectedEducationTitle: number | null;
  services: ServiceDto[] | null;
  subscriptions: SubscriptionDto[] | null;
  isLoaded: boolean;
  selectedSetting: "lessons" | "products" | "company" | "payments" | "calendar" | "education" | "education-category" | "education-title" | "reports" | "preferences" | "pro-subscription" | "support-info";
  addingLessonRatings: boolean;
  removingLessonRatings: boolean;
}

export const initialState: State = {
  educationCategories: null,
  educationTitles: null,
  educationBodies: null,
  lessonRatings: null,
  services: null,
  subscriptions: null,
  selectedEducationCategory: null,
  selectedEducationTitle: null,
  isLoaded: false,
  selectedSetting: "company",
  addingLessonRatings: false,
  removingLessonRatings: false,
};

const settingsReducer = createReducer(
  initialState,
  on(settingsActions.loadEducationCategories, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(
    settingsActions.loadEducationCategoriesSuccess,
    (state, { educationCategories }) => ({
      ...state,
      educationCategories,
      isLoaded: true,
    })
  ),
  on(settingsActions.addEducationCategory, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(
    settingsActions.addEducationCategorySuccess,
    (state, { educationCategory }) => ({
      ...state,
      educationCategories: [...state.educationCategories, educationCategory],
      isLoaded: true,
    })
  ),
  on(settingsActions.updateEducationCategory, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(settingsActions.updateEducationCategorySuccess, (state, { educationCategory }) => ({
    ...state,
    educationCategories: state.educationCategories.map(category =>
      category.education_category_id === educationCategory.education_category_id ? educationCategory : category
    ),
    isLoaded: true,
  })),
  on(settingsActions.deleteEducationCategory, (state, { educationCategory }) => ({
    ...state,
    educationCategories: state.educationCategories?.filter(
      category => category.education_category_id !== educationCategory.education_category_id
    )
  })),
  on(settingsActions.undoDeleteEducationCategory,
    (state, { educationCategory }) => ({
      ...state,
      educationCategories: [...state.educationCategories, educationCategory],
      isLoaded: true,
    })
  ),
  on(settingsActions.addEducationTitle, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(settingsActions.addEducationTitleSuccess, (state, { educationTitle }) => ({
    ...state,
    educationTitles: [...state.educationTitles, educationTitle],
    isLoaded: true,
  })),
  on(settingsActions.updateEducationTitle, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(settingsActions.updateEducationTitleSuccess, (state, { educationTitle }) => ({
    ...state,
    educationTitles: state.educationTitles.map(title =>
      title.education_title_id === educationTitle.education_title_id ? educationTitle : title
    ),
    isLoaded: true,
  })),
  on(settingsActions.deleteEducationTitle, (state, { educationTitle }) => ({
    ...state,
    educationTitles: state.educationTitles?.filter(
      title => title.education_title_id !== educationTitle.education_title_id
    )
  })),
  on(settingsActions.undoDeleteEducationTitle,
    (state, { educationTitle }) => ({
      ...state,
      educationTitles: [...state.educationTitles, educationTitle],
      isLoaded: true,
    })
  ),
  on(settingsActions.addEducationBody, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(settingsActions.addEducationBodySuccess, (state, { educationBody }) => ({
    ...state,
    educationBodies: [...state.educationBodies, educationBody],
    isLoaded: true,
  })),
  on(settingsActions.updateEducationBody, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(settingsActions.updateEducationBodySuccess, (state, { educationBody }) => ({
    ...state,
    educationBodies: state.educationBodies.map(body =>
      body.education_body_id === educationBody.education_body_id ? educationBody : body
    ),
    isLoaded: true,
  })),
  on(settingsActions.deleteEducationBody, (state, { educationBody }) => ({
    ...state,
    educationBodies: state.educationBodies?.filter(
      body => body.education_body_id !== educationBody.education_body_id
    )
  })),
  on(settingsActions.undoDeleteEducationBody,
    (state, { educationBody }) => ({
      ...state,
      educationBodies: [...state.educationBodies, educationBody],
      isLoaded: true,
    })
  ),
  on(
    settingsActions.selectEducationCategory,
    (state, { educationCategory }) => ({
      ...state,
      selectedEducationCategory: educationCategory.education_category_id,
    })
  ),
  on(settingsActions.selectEducationTitle, (state, { educationTitle }) => ({
    ...state,
    selectedEducationTitle: educationTitle.education_title_id,
  })),
  on(settingsActions.loadEducationTitles, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(
    settingsActions.loadEducationTitlesSuccess,
    (state, { educationTitles }) => ({
      ...state,
      educationTitles,
      isLoaded: true,
    })
  ),
  on(settingsActions.loadEducationBodies, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(
    settingsActions.loadEducationBodiesSuccess,
    (state, { educationBodies }) => ({
      ...state,
      educationBodies,
      isLoaded: true,
    })
  ),
  on(settingsActions.loadEducationTitlesForCategory, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(
    settingsActions.loadEducationTitlesForCategorySuccess,
    (state, { educationTitles }) => {
      const updatedEducationTitles = state.educationTitles.map(
        (existingTitle) => {
          const matchingTitle = educationTitles.find(
            (newTitle) =>
              newTitle.education_title_id === existingTitle.education_title_id
          );
          if (!matchingTitle) {
            return existingTitle;
          } else {
            return matchingTitle;
          }
        }
      );

      return {
        ...state,
        educationTitles: [...updatedEducationTitles],
        isLoaded: true,
      };
    }
  ),
  on(settingsActions.loadEducationBodiesForTitle, (state) => ({
    ...state,
    isLoaded: false,
  })),
  on(
    settingsActions.loadEducationBodiesForTitleSuccess,
    (state, { educationBodies }) => {
      const updatedEducationBodies = state.educationBodies.map(
        (existingBody) => {
          const matchingBody = educationBodies.find(
            (newBody) =>
              newBody.education_body_id === existingBody.education_body_id
          );
          if (!matchingBody) {
            return existingBody;
          } else {
            return matchingBody;
          }
        }
      );

      return {
        ...state,
        educationBodies: [...updatedEducationBodies],
        isLoaded: true,
      };
    }
  ),
  on(
    settingsActions.updateEducationTitlesSuccess,
    (state, { educationTitles }) => {
      const updatedEducationTitles = state.educationTitles.map(
        (existingTitle) => {
          const matchingTitle = educationTitles.find(
            (newTitle) =>
              newTitle.education_title_id === existingTitle.education_title_id
          );
          if (!matchingTitle) {
            return existingTitle;
          } else {
            return matchingTitle;
          }
        }
      );

      return {
        ...state,
        educationTitles: [...updatedEducationTitles],
        isLoaded: true,
      };
    }
  ),
  on(
    settingsActions.updateEducationBodiesSuccess,
    (state, { educationBodies }) => {
      const updatedEducationBodies = state.educationBodies.map(
        (existingBody) => {
          const matchingBody = educationBodies.find(
            (newBody) =>
              newBody.education_body_id === existingBody.education_body_id
          );
          if (!matchingBody) {
            return existingBody;
          } else {
            return matchingBody;
          }
        }
      );

      return {
        ...state,
        educationBodies: [...updatedEducationBodies],
        isLoaded: true,
      };
    }
  ),
  on(settingsActions.addLessonRatings, (state) => ({
    ...state,
    addingLessonRatings: true,
  })),
  on(settingsActions.addLessonRatingsSuccess, (state, { lessonRatings }) => {
    const updatedLessonRatings = state.lessonRatings?.map(
      (existingLessonRating) => {
        const matchingLessonRating = lessonRatings.find(
          (newLessonRating) =>
            newLessonRating.lesson_rating_id ===
            existingLessonRating.lesson_rating_id
        );
        if (!matchingLessonRating) {
          return existingLessonRating; // Keep existing lesson rating
        } else {
          return { ...existingLessonRating, ...matchingLessonRating }; // Update existing lesson rating
        }
      }
    ) ?? [];
  
    // Add new lesson ratings that don't exist in the current state
    const newLessonRatings = lessonRatings?.filter(
      (newLessonRating) =>
        !state.lessonRatings?.some(
          (existingLessonRating) =>
            existingLessonRating.lesson_rating_id ===
            newLessonRating.lesson_rating_id
        )
    ) ?? [];
  
    return {
      ...state,
      lessonRatings: [...updatedLessonRatings, ...newLessonRatings],
      addingLessonRatings: false,
      isLoaded: true,
    };
  }),
  on(errorActions.errorHandled, (state) => ({
    ...state,
    addingLessonRatings: false,
    removingLessonRatings: false,
  })),
  on(
    settingsActions.loadLessonRatingsSuccess,
    (state, { lessonRatings }) => ({
      ...state,
      lessonRatings,
      isLoaded: true,
    })
  ),
  on(
    settingsActions.loadLessonRatingsForLessonSuccess,
    (state, { lessonRatings }) => {
      const updatedLessonRatings = state.lessonRatings?.map(
        (existingLessonRating) => {
          const matchingLessonRating = lessonRatings.find(
            (newLessonRating) =>
              newLessonRating.lesson_rating_id ===
              existingLessonRating.lesson_rating_id
          );
          if (!matchingLessonRating) {
            return existingLessonRating; // Keep existing lesson rating
          } else {
            return { ...existingLessonRating, ...matchingLessonRating }; // Update existing lesson rating
          }
        }
      ) ?? [];
  
      // Add new lesson ratings that don't exist in the current state
      const newLessonRatings = lessonRatings?.filter(
        (newLessonRating) =>
          !state.lessonRatings?.some(
            (existingLessonRating) =>
              existingLessonRating.lesson_rating_id ===
              newLessonRating.lesson_rating_id
          )
      ) ?? [];
  
      return {
        ...state,
        lessonRatings: [...updatedLessonRatings, ...newLessonRatings],
        isLoaded: true,
      };
    }
  ),
  on(
    settingsActions.loadLessonRatingsForStudentSuccess,
    (state, { lessonRatings }) => {
      const updatedLessonRatings = state.lessonRatings?.map(
        (existingLessonRating) => {
          const matchingLessonRating = lessonRatings.find(
            (newLessonRating) =>
              newLessonRating.lesson_rating_id ===
              existingLessonRating.lesson_rating_id
          );
          if (!matchingLessonRating) {
            return existingLessonRating; // Keep existing lesson rating
          } else {
            return { ...existingLessonRating, ...matchingLessonRating }; // Update existing lesson rating
          }
        }
      ) ?? [];
  
      // Add new lesson ratings that don't exist in the current state
      const newLessonRatings = lessonRatings?.filter(
        (newLessonRating) =>
          !state.lessonRatings?.some(
            (existingLessonRating) =>
              existingLessonRating.lesson_rating_id ===
              newLessonRating.lesson_rating_id
          )
      ) ?? [];
  
      return {
        ...state,
        lessonRatings: [...updatedLessonRatings, ...newLessonRatings],
        isLoaded: true,
      };
    }
  ),
  on(settingsActions.removeLessonRatings, (state) => ({
    ...state,
    removingLessonRatings: true,
  })),
  on(
    settingsActions.removeLessonRatingsSuccess,
    (state, { lessonRatings }) => ({
      ...state,
      // remove the lessonRatings from the store
      lessonRatings: state.lessonRatings?.filter(
        (existingLessonRating) =>
          !lessonRatings.find(
            (newLessonRating) =>
              newLessonRating.lesson_rating_id ===
              existingLessonRating.lesson_rating_id
          )
      ),
      isLoaded: true,
      removingLessonRatings: false,
    })
  ),
  on(
    settingsActions.upsertLessonRatings,
    (state, { lessonRatings }) => ({
      ...state,
      lessonRatings: [
        ...state.lessonRatings?.filter(
          (existingLessonRating) =>
            !lessonRatings.find(
              (newLessonRating) =>
                newLessonRating.lesson_rating_id ===
                existingLessonRating.lesson_rating_id
            )
          ),
        ...lessonRatings,
        ],
    })
  ),
  on(
    settingsActions.loadServicesSuccess,
    (state, { services }) => ({
      ...state,
      services,
      isLoaded: true,
    })
  ),
  on(
    settingsActions.loadSubscriptionsSuccess,
    (state, { subscriptions }) => ({
      ...state,
      subscriptions,
      isLoaded: true,
    })
  ),
  on(settingsActions.upsertServiceSuccess, (state, { service }) => {
    const existingServiceIndex = state.services?.findIndex(
      (existingService) => existingService.service_id === service.service_id
    );

    let updatedServices;
    
    if (existingServiceIndex !== undefined && existingServiceIndex >= 0) {
      // Update existing service
      updatedServices = [
        ...state.services.slice(0, existingServiceIndex),
        service,
        ...state.services.slice(existingServiceIndex + 1),
      ];
    } else {
      // Add new service
      updatedServices = [...(state.services ?? []), service];
    }

    return {
      ...state,
      services: updatedServices,
      isLoaded: true,
    };
  }),
  on(settingsActions.upsertSubscriptionSuccess, (state, { subscription }) => {
    const existingSubscriptionIndex = state.subscriptions?.findIndex(
      (existingSubscription) => existingSubscription.subscription_id === subscription.subscription_id
    );

    let updatedSubscriptions;
    
    if (existingSubscriptionIndex !== undefined && existingSubscriptionIndex >= 0) {
      // Update existing service
      updatedSubscriptions = [
        ...state.subscriptions.slice(0, existingSubscriptionIndex),
        subscription,
        ...state.subscriptions.slice(existingSubscriptionIndex + 1),
      ];
    } else {
      // Add new service
      updatedSubscriptions = [...(state.subscriptions ?? []), subscription];
    }

    return {
      ...state,
      subscriptions: updatedSubscriptions,
      isLoaded: true,
    };
  }),
  on(settingsActions.deleteServiceSuccess, (state, { serviceId }) => {
    const updatedServices = state.services?.filter(
      (existingService) =>
        existingService.service_id !== serviceId
    );
    return {
      ...state,
      services: updatedServices,
      isLoaded: true,
    };
  }),
  on(settingsActions.deleteSubscriptionSuccess, (state, { subscriptionId }) => {
    const updatedSubscription = state.subscriptions?.filter(
      (existingSubscription) =>
        existingSubscription.subscription_id !== subscriptionId
    );
    return {
      ...state,
      subscriptions: updatedSubscription,
      isLoaded: true,
    };
  }),
  on(settingsActions.selectSetting, (state, { setting }) => ({
   ...state,
    selectedSetting: setting,
  }))
);

export function settingReducer(state: State | undefined, action: Action) {
  return settingsReducer(state, action);
}
